import stripeConfig from '@/Components/Payments/Stripe/StripeUtility.js';
import PledgeService from '@/Services/PledgeService';
import OrdersService from '@/Services/OrdersService';
import ReCaptchaProvider from '@/Services/ReCaptcha/ReCaptchaProvider';

import { apiClient } from '@/Clients/ApiClient';
import { useApiFetch } from '@/Composables/useApiFetch';
import { paymentDataSources, checkoutModes, paymentCollectionTypes, paymentProvider } from '@/Models';
import { ref, onMounted, reactive, computed, watch } from 'vue';
import { collectBrowserInfo } from '@/Utils/Browser.js';
import { useLanguageStore } from '@/Stores/Web/languageStore.js';
import { setGuestOrderToken } from '@/Components/Orders/OrderUtility';

import ProjectsAdminService from '@/Services/Admin/ProjectsAdminService.js';
import { useProjectContextStore } from '@/Stores/Web';

const captchaProvider = new ReCaptchaProvider();

const CHANGE_DEFERRED_PAYMENT = 'CHANGE_DEFERRED_PAYMENT';
const CREATE_DEFERRED_PAYMENT = 'CREATE_DEFERRED_PAYMENT';
const CREATE_INSTANT_PAYMENT = 'CREATE_INSTANT_PAYMENT';
const GRANT_ORDER_ACCESS = 'GRANT_ORDER_ACCESS';

export function useStripePayment({ publishableKey, effectivePayableAmount, isInBackupPaymentMode, currencyShortName } = {}) {
    const stripe = ref(null);
    const elements = ref(null);
    const paymentElement = ref(null);
    const paymentElementRef = ref(null);
    const isCaptchaEnabled = ref(false);
    const reCaptchaToken = ref(null);
    const isDeferredPaymentAllowed = ref(true);

    // These values are used only when first payment attempt fails
    // and we need to retry with a different checkout mode or order code,
    // in that case we save those values here to be used for the retry attempt
    const newCheckoutMode = ref(null);
    const newOrderCode = ref(null);

    const state = reactive({
        isProcessing: false,
        errorHandler: (error) => {
            throw error;
        },
    });

    const isProcessing = computed(() => state.isProcessing);

    const languageStore = useLanguageStore();
    const projectContextStore = useProjectContextStore();
    const { execute } = useApiFetch();

    const initialize = (errorHandler) => {
        state.errorHandler = errorHandler;
    };

    watch(currencyShortName, (newValue, oldValue) => {
        if (newValue && newValue !== oldValue && stripe.value) {
            elements.value.update(
                {
                    currency: newValue?.toLowerCase(),
                },
            );
        }
    });

    onMounted(async () => {
        await initializeStripe();
    });

    const pledgeService = new PledgeService(apiClient);
    const ordersService = new OrdersService(apiClient);
    const projectsAdminService = new ProjectsAdminService(apiClient);

    async function generateRecaptcha(action) {
        const token = await captchaProvider.getTokenAsync(action);
        reCaptchaToken.value = token;
    }

    const strategies = {
        backup: (payload) => pledgeService.saveProjectBackupPaymentMethod(payload),

        instant: (payload) =>
            pledgeService.createInstantPayment({
                ...payload,
                reCaptchaToken: reCaptchaToken.value,
                reCaptchaAction: CREATE_INSTANT_PAYMENT,
            }),

        instantCaptcha: async (payload) => {
            await generateRecaptcha(CREATE_INSTANT_PAYMENT);
            return strategies.instant(payload);
        },

        deferredCreate: (payload) =>
            pledgeService.createDeferredPayment({
                ...payload,
                reCaptchaToken: reCaptchaToken.value,
                reCaptchaAction: CREATE_DEFERRED_PAYMENT,
            }),

        deferredCreateCaptcha: async (payload) => {
            await generateRecaptcha(CREATE_DEFERRED_PAYMENT);
            return strategies.deferredCreate(payload);
        },

        deferredChange: (payload) =>
            pledgeService.changeDeferredPayment({
                ...payload,
                reCaptchaToken: reCaptchaToken.value,
                reCaptchaAction: CHANGE_DEFERRED_PAYMENT,
            }),

        deferredChangeCaptcha: async (payload) => {
            await generateRecaptcha(CHANGE_DEFERRED_PAYMENT);
            return strategies.deferredChange(payload);
        },
        manageShipping: (payload) => pledgeService.updateShippingDetails(payload),
    };

    function resolveStrategy(checkoutMode) {

        switch (checkoutMode) {
        case checkoutModes.storeCreateOrder:
        case checkoutModes.storeResumePayment:
        case checkoutModes.pledgeManagerResumePayment:
        case checkoutModes.pledgeManagerAmendOrder:
        case checkoutModes.latePledgeCreateOrder:
        case checkoutModes.pledgeManagerCreateOrder:
        case checkoutModes.crowdfundingAmendInstantOrder:
        case checkoutModes.latePledgeAmendOrder:
        case checkoutModes.latePledgeResumePayment:
        case checkoutModes.crowdfundingResumePayment:
            return isCaptchaEnabled.value ? 'instantCaptcha' : 'instant';

        case checkoutModes.crowdfundingCreateOrder:
        case checkoutModes.crowdfundingAmendDeferredOrder:
            if (isDeferredPaymentAllowed.value) {
                return isCaptchaEnabled.value ? 'deferredCreateCaptcha' : 'deferredCreate';
            }
            // Force instant payment when backend indicates it's required
            // (e.g., endgame active with guest users)
            return isCaptchaEnabled.value ? 'instantCaptcha' : 'instant';

        default:
            state.errorHandler('Unsupported checkout mode');
        }
    }

    const handleSubmit = async (params = {}) => {
        if (isInBackupPaymentMode.value) {
            const response = await saveProjectBackupPaymentMethod(params);
            return response;
        }
        await handleSubmitWithPayment(params);
    };

    const grantOrderAccess = async (email, projectID, createAccount) => {
        try {
            if (isCaptchaEnabled.value) {
                await generateRecaptcha(GRANT_ORDER_ACCESS);
            }
            const { data } = await pledgeService.grantOrderAccess(email, projectID, createAccount, reCaptchaToken.value, GRANT_ORDER_ACCESS);
            return data;
        }
        catch (error) {
            handleError(error);
        }
    };

    const handleSubmitWithPayment = async (params = {}) => {
        try {
            state.isProcessing = true;
            const { error: submitError } = await elements.value.submit();
            if (submitError) {
                handleError(submitError);
                return;
            }

            const { paymentMethod, error } = await stripe.value.createPaymentMethod({
                elements: elements.value,
            });
            if (error) {
                handleError(error);
                return;
            }

            const payload = {
                browserInfo: collectBrowserInfo(),
                paymentMethod: {
                    paymentToken: paymentMethod.id,
                    type: 'scheme',
                    paymentDataSource: paymentDataSources.newCard,
                    // this is required to pass validation on backend but not used for stripe payments
                    ...stripeConfig.validationPlaceholders,
                },
                ...params,
                checkoutMode: newCheckoutMode.value || params.checkoutMode,
                orderCode: newOrderCode.value || params.orderCode,
            };

            const key = resolveStrategy(newCheckoutMode.value || params.checkoutMode);

            return await execute(
                (signal) => strategies[key](payload),
                {
                    cache: {
                        enabled: false,
                        key: null,
                        skipCache: true,
                    },
                    onSuccess: async ({ data }) => {
                        if (data) {
                            await handleSuccess(data, newCheckoutMode.value || params.checkoutMode, params.projectID);
                            state.isProcessing = false;
                        }
                    },
                    onError: (errorObject) => {
                        const { error } = errorObject;
                        const errorMessage = error?.response?.data?.message || error.message || 'Payment processing error';
                        state.isProcessing = false;
                        state.errorHandler(errorMessage);
                    },
                },
            );
        }
        catch (e) {
            handleError(e);
        }
    };

    const handleSuccess = async ({ data }, checkoutMode, projectID) => {
        state.isProcessing = true;
        const result = data.data || {};
        const isInDeferredMode = isDeferredPaymentAllowed.value && [
            checkoutModes.crowdfundingCreateOrder,
            checkoutModes.crowdfundingAmendDeferredOrder,
        ].includes(checkoutMode);
        if (result.guestOrderToken) {
            setGuestOrderToken(result.guestOrderToken);
        }
        newOrderCode.value = result.orderCode;

        if (result?.paymentAction?.type === 'redirect' && result?.paymentAction?.token) {
            await handleRedirectAction(result.paymentAction.token);
        }
        else if (result?.paymentAction?.type === 'confirmation' && result?.paymentAction?.token) {
            if (isInDeferredMode || isInBackupPaymentMode.value) {
                const data = await confirmSetupAction(elements, result.paymentAction.token, result.paymentAction.url);
                if (isInBackupPaymentMode.value) {
                    await handleAuthorizeProjectBackupPaymentMethod(data, projectID);
                }
            }
            else {
                await confirmPaymentAction(elements, result, projectID);
            }
        }
        else if (data?.success) {
            window.location = data.data.redirectUrl;
        }
    };

    const confirmSetupAction = async (elements, clientSecret, returnUrl) => {
        const params = isInBackupPaymentMode.value
            ? { redirect: 'if_required' }
            : { confirmParams: {
                return_url: returnUrl },
            };
        const data = await stripe.value.confirmSetup({
            elements: elements.value,
            clientSecret,
            ...params,
        });
        if (data.error) {
            handleError(data.error);
        }
        return data;
    };

    const confirmPaymentAction = async (elements, result, projectID) => {
        const { error } = await stripe.value.confirmPayment({
            elements: elements.value,
            clientSecret: result.paymentAction.token,
            confirmParams: {
                return_url: result.paymentAction.url,
            },
        });
        if (error) {
            const response = await abortPayment(result.orderCode, projectID);
            if (response.data.newCheckoutMode) {
                newCheckoutMode.value = response.data.newCheckoutMode;
            }
            handleError(error);
        }
    };

    const handleRedirectAction = async (clientSecret) => {
        const { error } = await stripe.value.handleNextAction({
            clientSecret,
        });
        if (error) {
            handleError(error);
        }
    };

    const handleError = (error) => {
        state.errorHandler(error);
        state.isProcessing = false;
    };

    const saveProjectBackupPaymentMethod = async (params) => {
        try {
            state.isProcessing = true;
            const { error: submitError } = await elements.value.submit();
            if (submitError) {
                handleError(submitError);
                return;
            }

            const { paymentMethod, error } = await stripe.value.createPaymentMethod({
                elements: elements.value,
            });
            if (error) {
                handleError(error);
                return;
            }

            const payload = {
                browserInfo: collectBrowserInfo(),
                paymentMethod: {
                    paymentToken: paymentMethod.id,
                    type: 'scheme',
                    paymentDataSource: paymentDataSources.newCard,
                    // this is required to pass validation on backend but not used for stripe payments
                    ...stripeConfig.validationPlaceholders,
                },
                ...params,
            };

            return await execute(
                (signal) => projectsAdminService.saveProjectBackupPaymentMethod(payload, params.projectID),
                {
                    onSuccess: async ({ data }) => {
                        await handleSuccess(data, params.checkoutMode, params.projectID);
                        state.isProcessing = false;
                        return {
                            success: true,
                        };
                    },
                    onError: (error) => {
                        handleError(error);
                    },
                },
            );
        }
        catch (e) {
            handleError(e);
        }
    };
    const handleAuthorizeProjectBackupPaymentMethod = async (response, projectID) => {
        try {
            state.isProcessing = true;
            return await projectsAdminService.authorizeProjectBackupPaymentMethod({
                threeDSResult: 'threeDSResult_true',
                projectID,
                providerPaymentID: response.setupIntent.id,
            });
        }
        catch (e) {
            handleError(e);
        }
    };

    const loadPaymentConfiguration = async () => {
        const projectID = projectContextStore.projectContext.projectID;
        if (projectID) {
            const { data } = await pledgeService.getAvailablePaymentMethods({
                projectID,
                paymentProvider: paymentProvider.stripeV2,
            });
            const availableCollectionTypes = data.map(pm => pm.paymentCollectionType);
            isDeferredPaymentAllowed.value = availableCollectionTypes.includes(paymentCollectionTypes.deferred);
        }
    };

    const initializeStripe = async () => {
        try {
            state.isProcessing = true;
            loadPaymentConfiguration();
            const { loadStripe } = await import('@stripe/stripe-js');
            isCaptchaEnabled.value = await captchaProvider.initAsync();
            if (effectivePayableAmount.value > 0 || isInBackupPaymentMode.value) {
                stripe.value = await loadStripe(publishableKey, {
                    locale: languageStore.selectedLanguage,
                });

                elements.value = stripe.value.elements({
                    mode: 'setup',
                    paymentMethodTypes: ['card'],
                    paymentMethodCreation: 'manual',
                    appearance: stripeConfig.appearance,
                    currency: currencyShortName.value?.toLowerCase(),
                });

                paymentElement.value = elements.value.create('payment',
                    {
                        layout: stripeConfig.layout,
                    },
                );
                if (paymentElementRef.value) {
                    paymentElement.value.mount(paymentElementRef.value);
                }
            }
        }
        catch (e) {
            throw new Error(e);
        }
        finally {
            state.isProcessing = false;
        }
    };

    const submitChangedOrder = async (orderCode, projectID) => {
        try {
            state.isProcessing = true;
            const { data } = orderCode
                ? await pledgeService.amendPaidPledgeWithoutPayment(orderCode, projectID)
                : await pledgeService.createPledgeWithoutPayment(projectID);

            window.location = data.returnUrl;
        }
        catch (error) {
            state.errorHandler(error);
            state.isProcessing = false;
        }
    };

    const abortPayment = async (orderCode, projectID) => {
        try {
            state.isProcessing = true;
            const { data } = await ordersService.abortPayment(orderCode, projectID);
            return data;
        }
        catch (error) {
            state.errorHandler(error);
        }
        finally {
            state.isProcessing = false;
        }
    };

    const setIsProcessing = (value) => {
        state.isProcessing = value;
    };

    return {
        paymentElementRef,
        handleSubmit,
        grantOrderAccess,
        isProcessing,
        initialize,
        initializeStripe,
        submitChangedOrder,
        isCaptchaEnabled,
        setIsProcessing,
    };
}
